/**
 * criteriaRows — the PURE rule for writing a list of criterion texts into the
 * rows the form already has (PRD `2026-10-06-ai-review-context-prompt-viewer`,
 * C1).
 *
 * A row's `id` is its React key. Every programmatic write (the import-change
 * reset, the source-review prefill, the `?criteriaFrom=` carry) used to build a
 * whole new array of freshly minted ids, so React unmounted every row and
 * mounted new ones: a flicker, focus and caret gone, the field's undo history
 * gone. Reconciling BY POSITION keeps the nodes: row N keeps its id and only
 * its text changes; ids are minted only for rows beyond what was there; rows
 * past the new length are dropped.
 *
 * By position rather than by text on purpose. Matching on text would MOVE a
 * row's node when the order changes, and moving a focused element out of and
 * back into the DOM blurs it — the exact loss this exists to prevent.
 */
import type { Criterion } from "./CriteriaPanel";

let criterionIdSeq = 0;
/** Deterministic, collision-free row id — no crypto/UUID dependency needed. */
export function nextCriterionId(): string {
  criterionIdSeq += 1;
  return `criterion-${criterionIdSeq}`;
}

/** The blank form: one empty row. */
export function emptyCriteria(): Criterion[] {
  return [{ id: nextCriterionId(), text: "" }];
}

/**
 * @param existing  the rows the form holds now (not mutated)
 * @param texts     what the rows should say, in order; the result has exactly
 *                  this many rows
 * @param nextId    id factory for rows beyond `existing.length`
 */
export function reconcileCriteria(
  existing: readonly Criterion[],
  texts: readonly string[],
  nextId: () => string = nextCriterionId
): Criterion[] {
  return texts.map((text, i) => {
    const current = existing[i];
    if (!current) return { id: nextId(), text };
    return current.text === text ? current : { id: current.id, text };
  });
}
